import * as React from 'react';
import { getTheme, mergeStyleSets } from '@fluentui/react';

export interface ISearchHighlightProps {
    text?: string | number;
    /** Søgeord i små bogstaver. Alle forekomster af hvert ord fremhæves */
    tokens: string[];
}

const theme = getTheme();

const classNames = mergeStyleSets({
    mark: {
        backgroundColor: theme.palette.themeLighter,
        color: theme.palette.themeDarker,
        fontWeight: 600,
        borderRadius: '2px',
        padding: '0 1px',
    },
});

/** Finder alle forekomster af søgeordene og slår overlappende forekomster sammen */
const getRanges = (text: string, tokens: string[]): [number, number][] => {
    const lower = text.toLocaleLowerCase();
    const ranges: [number, number][] = [];
    tokens.forEach(token => {
        let index = lower.indexOf(token);
        while (index > -1) {
            ranges.push([index, index + token.length]);
            index = lower.indexOf(token, index + token.length);
        }
    });
    ranges.sort((a, b) => a[0] - b[0]);

    const merged: [number, number][] = [];
    ranges.forEach(range => {
        const last = merged[merged.length - 1];
        if (last && range[0] <= last[1]) {
            last[1] = Math.max(last[1], range[1]);
        } else {
            merged.push([range[0], range[1]]);
        }
    });
    return merged;
};

const SearchHighlight: React.FunctionComponent<ISearchHighlightProps> = ({ text, tokens }) => {
    const value = (text ?? "") + "";
    const ranges = tokens.length > 0 && value !== "" ? getRanges(value, tokens) : [];
    if (ranges.length === 0) return <>{value}</>;

    const parts: React.ReactNode[] = [];
    let position = 0;
    ranges.forEach(([start, end]) => {
        if (start > position) parts.push(value.substring(position, start));
        parts.push(<mark key={start} className={classNames.mark}>{value.substring(start, end)}</mark>);
        position = end;
    });
    if (position < value.length) parts.push(value.substring(position));

    return <>{parts}</>;
};

export default SearchHighlight;
